import { useEffect, useRef, useState, useCallback } from "react";
import { wsUrl } from "@/lib/apiClient";

// Owns the viewer WebSocket: connection lifecycle, reconnect, heartbeat,
// frame/status/fps/blocked state, and the outbound `send` helper.
export function useRemoteSession(machineId) {
  const wsRef = useRef(null);
  const frameCount = useRef(0);
  const [frame, setFrame] = useState(null);
  const [status, setStatus] = useState("connecting"); // connecting | online | offline
  const [fps, setFps] = useState(0);
  const [blocked, setBlocked] = useState(null); // { code, message } when connection refused
  const [sameNetwork, setSameNetwork] = useState(false); // viewer shares the agent's public IP

  useEffect(() => {
    let closedByUs = false;
    let reconnectTimer = null;
    let heartbeatId = null;

    const clearHeartbeat = () => {
      if (heartbeatId) { clearInterval(heartbeatId); heartbeatId = null; }
    };

    const connect = () => {
      const url = wsUrl(`/api/ws/viewer/${machineId}`);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onmessage = (e) => {
        let msg;
        try { msg = JSON.parse(e.data); } catch { return; }
        if (msg.type === "frame") {
          setFrame(`data:image/jpeg;base64,${msg.data}`);
          frameCount.current += 1;
        } else if (msg.type === "agent_status") {
          setStatus(msg.online ? "online" : "offline");
        } else if (msg.type === "same_network") {
          setSameNetwork(true);
        }
      };
      ws.onopen = () => {
        setStatus((s) => (s === "connecting" ? "offline" : s));
        // Heartbeat keeps the connection (and its upstream half) alive so control never silently dies.
        clearHeartbeat();
        heartbeatId = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
        }, 15000);
      };
      ws.onclose = (event) => {
        clearHeartbeat();
        // Server refused the connection for a policy reason — don't auto-reconnect.
        if (event.code === 4004) {
          closedByUs = true;
          setStatus("offline");
          setBlocked({ code: 4004, message: "Concurrent session limit reached for your plan. Close another active session or upgrade in Billing." });
          return;
        }
        if (event.code === 4003) {
          closedByUs = true;
          setStatus("offline");
          setBlocked({ code: 4003, message: "You don't have access to this machine." });
          return;
        }
        // Routine drop (the cloud ingress recycles the socket ~every 60s). Reconnect fast
        // and KEEP the last frame + status so the session feels continuous — the server
        // re-sends the true agent_status on reconnect and only reports offline after a grace.
        if (!closedByUs) {
          reconnectTimer = setTimeout(connect, 500);
        }
      };
      ws.onerror = () => { try { ws.close(); } catch { /* socket already closed */ } };
    };

    connect();

    const fpsTimer = setInterval(() => {
      setFps(frameCount.current);
      frameCount.current = 0;
    }, 1000);

    return () => {
      closedByUs = true;
      clearInterval(fpsTimer);
      clearHeartbeat();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* socket already closed */ } }
    };
  }, [machineId]);

  const send = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj));
  }, []);

  return { frame, setFrame, status, fps, blocked, sameNetwork, send };
}
